import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { argbFromHex } from '@material/material-color-utilities'

import { BrandKitsAPI, OrganizationsAPI } from '@makyoapp/makyo-sdk'
import { fromSdkOrg } from '@makyoapp/makyo-common'
import { DEFAULT_MATERIAL_TYPOGRAPHY } from '@makyoapp/makyo-tw-ui'

import { toasted } from '@/lib/toast'
import { DEFAULT_EXTENDED_COLORS } from '@/shared/brand-kit'
import { ORG_ROUTE } from '@/shared/constants'

import { NewOrgTeamModalSubmit } from '../components/NewOrgTeamModalForm'
import { useOrgsContext } from '../context/OrgsContext'
import { useReqError } from './useReqError'

/**
 * A hook that returns a callback function for adding a new organization.
 *
 * @param onComplete - A function that is called when the add operation is complete.
 * @returns A callback function that takes two arguments: a new organization object
 * and a done function. The new organization object has a `name` property and an
 * optional `avatar` property. The done function is a callback that should be called
 * when the add operation is finished.
 */
export function useAddOrgCb(onComplete: () => void) {
  const printAPIError = useReqError()
  const { updateOrgsPerms, updateOrgs } = useOrgsContext()
  const { t } = useTranslation()
  const navigate = useNavigate()

  return useCallback(
    (newOrg: NewOrgTeamModalSubmit, done: () => void) => {
      OrganizationsAPI.ReqAdd({
        _Avatar: newOrg.brandKit?.type === 'image' ? newOrg.brandKit?.image.file || null : null,
        _Name: newOrg.name,
        _Description: '',
        _Members: [],
      })
        .then((res) => {
          updateOrgsPerms([fromSdkOrg(res)])
          toasted(t('Organization created!'))
          if (newOrg.brandKit) {
            const split = newOrg.brandKit.image.file ? newOrg.brandKit.image.file.name.split('.') : []
            const fileExt = split.length > 0 ? split[split.length - 1] : ''
            return BrandKitsAPI.ReqAdd({
              _Logo: newOrg.brandKit.type === 'image' ? (newOrg.brandKit.image.file ?? null) : null,
              _Name: 'Default',
              _Colors: [
                {
                  _Value: argbFromHex(newOrg.brandKit.color),
                  _Name: 'Default',
                  _IsDefault: true,
                  _CustomColors: DEFAULT_EXTENDED_COLORS,
                },
              ],
              _OrgID: res._id,
              _FontsFamily: Object.fromEntries(
                Object.entries(DEFAULT_MATERIAL_TYPOGRAPHY).map(([k, v]) => {
                  return [k, JSON.stringify(v)]
                })
              ),
              _LogoFileExt: fileExt,
            }).then(() => {
              return updateOrgs().then(() => {
                navigate(`${ORG_ROUTE}/${res._id}`)
              })
            })
          }
          return updateOrgs().then(() => {
            navigate(`${ORG_ROUTE}/${res._id}`)
          })
        })
        .catch(printAPIError)
        .finally(() => {
          done()
          onComplete()
        })
    },
    [onComplete, navigate, t, updateOrgs, updateOrgsPerms, printAPIError]
  )
}
